/**
 * Is this build allowed to preview a design other than the tenant's own?
 *
 * The design override used to be gated on `NODE_ENV !== "production"`, which
 * reads as "not production" but does not mean that. Create React App sets
 * NODE_ENV to "production" for every build it emits — the dev deployment
 * included — so the gate was true only while running `yarn start` on someone's
 * laptop. On dev, `?design=design3` silently did nothing, which is why the
 * template could not be looked at anywhere a colleague could reach.
 *
 * REACT_APP_ENV is the thing that actually names the environment: the pipeline
 * exports it per stage (see buildspec-s3.yml and api/auth/auth.ts, which picks
 * its API host from the same value). Production leaves it unset.
 *
 * Stage is deliberately excluded along with production. Stage fronts real
 * client sites, and one build serves every tenant, so a preview control there
 * is a control on somebody's live-looking site.
 *
 * Written as three comparisons rather than an array lookup on purpose. CRA
 * inlines both variables at build time, and the pipeline always exports
 * REACT_APP_ENV (buildspec-s3.yml: `REACT_APP_ENV=$ENV`), so on a stage or
 * production build the whole expression is a constant the minifier folds to
 * `false` — and what is rendered behind it, the switcher included, drops out
 * of the bundle rather than shipping as dead code (1NOW-10601). An
 * `includes()` on an array literal is a call the minifier will not evaluate,
 * which is what kept the switcher in every production bundle before.
 */
export const devPreviewEnabled = (): boolean =>
  process.env.NODE_ENV !== "production" ||
  process.env.REACT_APP_ENV === "dev" ||
  process.env.REACT_APP_ENV === "qa" ||
  process.env.REACT_APP_ENV === "local";

/**
 * Is the floating Template switcher shown?
 *
 * Only on a developer's own machine: `yarn start`, or a build pointed at a
 * local backend. designa, designb and devtmp1 are all served by the dev build,
 * and designa/designb are the public demo sites the dashboard links operators
 * to, so a dev-only gate put a "C Template" pill on a page shown to customers
 * (1NOW-11071). QA builds already hid it.
 *
 * Previewing a design on dev and QA does not need it: `?design=design3` (or
 * design1/design2) still works there — devPreviewEnabled above is unchanged —
 * and it is what the dashboard's Live Preview opens. An empty `?design=`
 * returns the tab to the tenant's own template.
 *
 * Constant on every deployed build (NODE_ENV is "production" and the pipeline
 * sets REACT_APP_ENV to dev/qa/stage or leaves it unset), so the minifier
 * drops the switcher from all of their bundles, not only stage's and
 * production's.
 */
export const devSwitcherEnabled = (): boolean =>
  process.env.NODE_ENV !== "production" || process.env.REACT_APP_ENV === "local";

/** The templates, in the order the switcher lists them. */
export const DESIGNS: { code: string; name: string; letter: string }[] = [
  { code: "design1", name: "Classic", letter: "A" },
  { code: "design2", name: "Modern", letter: "B" },
];

export const DESIGN_STORAGE_KEY = "dc_dev_design";
export const COMPANY_STORAGE_KEY = "dc_dev_company";

/**
 * The design a dev preview is showing, or null when the tenant's own applies.
 *
 * App picks its router from this override (App.tsx, devDesign), but the two
 * content reads — /api/website/details/ and /api/website/sections/ — sent only
 * the company, so the backend answered them from the tenant's default
 * partition. Previewing Design A or B from the switcher on a Design C tenant
 * therefore drew those templates over Design C's content, which is nearly
 * empty until someone copies content across, and QA reopened 1NOW-10558
 * because the previews came out blank. The same mismatch in the other
 * direction painted Design C with the default design's colours on a tenant
 * whose own template is B. The backend already honours ?design= on both
 * reads, so the preview now asks for the partition it is drawing.
 *
 * A pure read of the same sources App consults, in the same order: `?design=`
 * first (empty clears the override), then the tab's stored choice. App keeps the side effects (persisting and
 * clearing the stored choice); this only reads. Outside dev, qa and local
 * builds it is the constant null, so stage and production send exactly the
 * requests they always did.
 */
export const previewDesign = (
  search: string = typeof window !== "undefined" ? window.location.search : "",
): string | null => {
  if (!devPreviewEnabled()) return null;
  let raw: string | null = null;
  let stored: string | null = null;
  try {
    raw = new URLSearchParams(search).get("design");
    stored = sessionStorage.getItem(DESIGN_STORAGE_KEY);
  } catch {
    return null;
  }
  if (raw !== null && raw.trim() === "") return null;
  const chosen = (raw ?? stored ?? "").trim();
  if (chosen) return chosen;
  return null;
};

/**
 * The `design` param for a content read: the previewed design when it is one
 * of the three the backend knows, otherwise nothing, so an unknown override
 * never turns into a request the backend would reject.
 */
export const previewDesignParam = (): { design?: string } => {
  const code = previewDesign();
  return code && DESIGNS.some((d) => d.code === code) ? { design: code } : {};
};
